import { SparklesIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isPropagationProposal, Turn } from "../../util/slopifyThread";
import HistoryAccordion from "./HistoryAccordion";
import InFlightCard from "./InFlightCard";
import FailedTurnCard from "./FailedTurnCard";
import ProposalCard from "./ProposalCard";
import PropagationCard from "./PropagationCard";
import RestoreConfirmDialog from "./RestoreConfirmDialog";

interface ThreadAreaProps {
  inFlightTurn?: Turn;
  liveTurn?: Turn;
  historyTurns?: Turn[];
  failedTurns?: Turn[];
  manuallyDirtied: Set<string>;
  activeLanguageId: number;
  onAbort?: () => void;
  onRetry?: (turn: Turn) => void;
  onAccept?: (turnId: string, fieldNames: string[]) => void;
  onReject?: (turnId: string) => void;
  onRefine?: (turn: Turn) => void;
  onRestore?: (turnId: string) => void;
  onApplyLanguage?: (turnId: string, languageId: number) => void;
  onRejectLanguage?: (turnId: string, languageId: number) => void;
  onApplyAllLanguages?: (turnId: string) => void;
  sourceValues?: Record<string, unknown>;
  canPropagate?: boolean;
  targetLanguageIds?: number[];
  staleFieldsByLanguageId?: Record<number, Set<string>>;
  onPropagateAccepted?: (turn: Turn) => void;
}

export const ThreadArea = ({
  inFlightTurn,
  liveTurn,
  historyTurns = [],
  failedTurns = [],
  manuallyDirtied,
  activeLanguageId,
  onAbort,
  onRetry,
  onAccept,
  onReject,
  onRefine,
  onRestore,
  onApplyLanguage,
  onRejectLanguage,
  onApplyAllLanguages,
  sourceValues = {},
  canPropagate = false,
  targetLanguageIds = [],
  staleFieldsByLanguageId: _staleFieldsByLanguageId = {},
  onPropagateAccepted,
}: ThreadAreaProps) => {
  const { t } = useTranslation();
  const [pendingRestore, setPendingRestore] = useState<Turn | null>(null);

  const hasContent =
    Boolean(inFlightTurn) || Boolean(liveTurn) || historyTurns.length > 0 || failedTurns.length > 0;

  if (!hasContent) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-6 px-3 text-gray-500">
        <SparklesIcon className="h-6 w-6 text-green-300 mb-2" />
        <div className="text-xs font-medium text-gray-700">
          {t("general.assistantScenarIa.threadEmptyTitle")}
        </div>
        <div className="text-[11px] text-gray-500 mt-1">
          {t("general.assistantScenarIa.threadEmptySubtitle")}
        </div>
      </div>
    );
  }

  const propagateableAccepted =
    canPropagate && targetLanguageIds.length > 0 ? onPropagateAccepted : undefined;

  return (
    <div className="py-2">
      {liveTurn && isPropagationProposal(liveTurn.proposal)
        ? onApplyLanguage &&
          onRejectLanguage &&
          onApplyAllLanguages && (
            <PropagationCard
              key={liveTurn.id}
              turn={liveTurn}
              sourceValues={sourceValues}
              onApplyLanguage={onApplyLanguage}
              onRejectLanguage={onRejectLanguage}
              onApplyAll={onApplyAllLanguages}
            />
          )
        : liveTurn &&
          onAccept &&
          onReject &&
          onRefine && (
            <ProposalCard
              key={liveTurn.id}
              turn={liveTurn}
              manuallyDirtied={manuallyDirtied}
              onAccept={onAccept}
              onReject={onReject}
              onRefine={onRefine}
            />
          )}
      {inFlightTurn && (
        <InFlightCard prompt={inFlightTurn.prompt} onAbort={onAbort ?? (() => undefined)} />
      )}
      {failedTurns.map((turn) => (
        <FailedTurnCard key={turn.id} turn={turn} onRetry={onRetry ?? (() => undefined)} />
      ))}
      {historyTurns.length > 0 && (
        <HistoryAccordion
          turns={historyTurns}
          activeLanguageId={activeLanguageId}
          onRestore={(turn) => setPendingRestore(turn)}
          onRetry={onRetry}
          onPropagateAccepted={propagateableAccepted}
          canPropagate={canPropagate}
        />
      )}
      {pendingRestore && (
        <RestoreConfirmDialog
          turn={pendingRestore}
          currentFormValues={sourceValues}
          onCancel={() => setPendingRestore(null)}
          onConfirm={() => {
            const turn = pendingRestore;
            setPendingRestore(null);
            if (turn && onRestore) onRestore(turn.id);
          }}
        />
      )}
    </div>
  );
};

export default ThreadArea;
